import { FC, ReactElement, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";
import { postWithoutSign } from "../services/ApiHandler";
import { getReferrer, saveToken } from "../services/CookieHandler";
import { useDisclosure } from "@mantine/hooks";
import { Center, Checkbox, Modal } from "@mantine/core";

export class LoginDialogProps {
  onLogin?: () => void;
  onCancel?: () => void;
  isLogin?: boolean = true;
}

export const LoginDialog: FC<LoginDialogProps> = ({ onLogin, onCancel, isLogin }) => {
  const [isLoginMode, setIsLoginMode] = useState(isLogin);
  const [tosChecked,  setTosChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const appleInitialized = useRef(false);
  const hasAgreedToPolicies = tosChecked && privacyChecked;

  function GoogleAuthComponent() {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID!}>
        <GoogleAuthComponentLogin />
      </GoogleOAuthProvider>
    );
  }

  
  async function loginToSite(token: String) {
    var param = { AccessToken: token };
    var response = await postWithoutSign<string>('GoogleAuth/Login', param);
    if(response.code === 1) {
      saveToken(response.data!);
      toast.success("ログインしました");
      onLogin!();
    } else {
      toast.error(response.message!);
    }
  }

  async function register(token: String) {
    var param = { AccessToken: token, Referrer: getReferrer() };
    var response = await postWithoutSign<string>('GoogleAuth/Register', param);
    if(response.code === 1) {
      saveToken(response.data!);
      // toast.success("ログインしました");
      // onLogin!();
      window.location.href = '/thxarigatoo_accountregistration';
    } else {
      toast.error(response.message!);
    }
  }

  async function appleLogin() {
    try {
      const AppleID = (window as any).AppleID;
      if (!AppleID) {
        toast.error('Apple Sign inのスクリプトが読み込めませんでした');
        return;
      }

      const clientId = process.env.REACT_APP_APPLE_CLIENT_ID!;
      const redirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI!;
      if (!clientId || !redirectURI) {
        toast.error('Apple認証の設定が未構成です');
        return;
      }

      if (!appleInitialized.current) {
        AppleID.auth.init({
          clientId: clientId,
          scope: 'name email',
          redirectURI: redirectURI,
          usePopup: true,
        });
        appleInitialized.current = true;
      }

      const result = await AppleID.auth.signIn();
      const authCode = result?.authorization?.code;
      const identityToken = result?.authorization?.id_token;
      if (!authCode || !identityToken) {
        toast.error('Apple認証に失敗しました');
        return;
      }

      const response = await postWithoutSign<string>('AppleAuth/Login', {
        authCode: authCode,
        identityToken: identityToken,
      });
      if (response.code === 1) {
        saveToken(response.data!);
        toast.success('ログインしました');
        onLogin?.();
      } else {
        toast.error(response.message!);
      }
    } catch (e) {
      console.error(e);
      toast.error('Apple認証がキャンセルまたは失敗しました');
    }
  }

  async function appleRegister() {
    try {
      const AppleID = (window as any).AppleID;
      if (!AppleID) {
        toast.error('Apple Sign inのスクリプトが読み込めませんでした');
        return;
      }

      const clientId = process.env.REACT_APP_APPLE_CLIENT_ID!;
      const redirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI!;
      if (!clientId || !redirectURI) {
        toast.error('Apple認証の設定が未構成です');
        return;
      }

      if (!appleInitialized.current) {
        AppleID.auth.init({
          clientId: clientId,
          scope: 'name email',
          redirectURI: redirectURI,
          usePopup: true,
        });
        appleInitialized.current = true;
      }

      const result = await AppleID.auth.signIn();
      const authCode = result?.authorization?.code;
      const identityToken = result?.authorization?.id_token;
      if (!authCode || !identityToken) {
        toast.error('Apple認証に失敗しました');
        return;
      }

      const response = await postWithoutSign<string>('AppleAuth/Register', {
        authCode: authCode,
        identityToken: identityToken,
      });
      if (response.code === 1) {
        saveToken(response.data!);
        window.location.href = '/thxarigatoo_accountregistration';
      } else {
        toast.error(response.message!);
      }
    } catch (e) {
      console.error(e);
      toast.error('Apple認証がキャンセルまたは失敗しました');
    }
  }

  function GoogleAuthComponentLogin() {
    const login = useGoogleLogin({
      onSuccess: (codeResponse) => isLoginMode ? loginToSite(codeResponse.access_token) : register(codeResponse.access_token), // 認証コードを取得
      onError:(ex) => console.log(ex),
      scope: "email openid", // scopeはスペース区切り
    });

    return (
      <div className="mr-4 text-center">
        <button disabled={!isLoginMode && !hasAgreedToPolicies} color="primary" onClick={(e) => login()} className="mx-auto max-w-[300px] flex border py-1 px-2 w-full justify-center bg-white">
          <img className="w-[24px] mr-2" src="/images/icons8-google-48.png"></img>
          <span className="text-sm">{ isLoginMode ? 'Googleでログイン' : 'Googleで登録' }</span>
        </button>
      </div>
    );
  }

  return <>
    <div className="w-full sm:w-[800px] text-primary">
      <div className="block sm:flex mb-2">
        { isLoginMode && 
        <div className="sm:min-w-[500px] text-center mt-auto mb-auto pb-8">
          <img src="/images/drop-ai-logo.png" className="w-[120px] inline"></img>
          <div className="text-xl font-bold mt-4 mb-4">アカウントにログイン</div>
          {GoogleAuthComponent()}
          <div className="mr-4 text-center mt-3">
            <button
              onClick={appleLogin}
              className="mx-auto max-w-[300px] flex py-2 px-2 w-full justify-center items-center bg-white text-black border rounded"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 14 17"
                width="18"
                height="18"
                fill="currentColor"
                className="mr-2"
                aria-hidden="true"
              >
                <path d="M10.243 9.088c-.015-1.626 1.328-2.403 1.387-2.44-.757-1.1-1.934-1.25-2.352-1.267-1-.102-1.95.586-2.457.586-.514 0-1.286-.572-2.12-.557-1.089.015-2.095.634-2.656 1.607-1.135 1.965-.29 4.862.815 6.455.54.785 1.18 1.662 2.02 1.632.815-.03 1.12-.525 2.103-.525.976 0 1.26.525 2.12.51.876-.015 1.432-.8 1.971-1.592.62-.907.876-1.78.892-1.826-.02-.007-1.707-.653-1.724-2.083zM8.72 3.96c.45-.544.756-1.3.674-2.06-.653.026-1.438.435-1.901.979-.418.482-.784 1.247-.687 1.987.728.056 1.464-.37 1.914-.905z"/>
              </svg>
              <span className="text-sm">Appleでログイン</span>
            </button>
          </div>
          <div className="mt-8">
            <span onClick={() => setIsLoginMode(false) } className="text-sm clickable text-blue-600">アカウントをお持ちではない方はこちら</span>
          </div>
        </div>
        }
        { !isLoginMode && 
        <div className="sm:min-w-[500px] text-center mt-auto mb-auto pb-8">
          <div className="mt-8 mb-2">
            <img src="/images/drop-ai-logo.png" className="w-[120px] inline"></img>
            <div className="text-xl font-bold mt-4 mb-4">アカウントに登録して<br/>
              <span>¥0円</span>
              <span className="text-xs font-normal">(無料)※</span>ではじめる</div>
            <div className="flex flex-col items-start mt-8 space-y-2 mx-auto w-fit">
              <Checkbox
                  color="#0909FF"
                  checked={tosChecked}
                  label={
                    <span>
                      <a className="text-blue-600 underline" href="https://drop4i.com/terms-of-service" target="_blank" rel="noopener noreferrer">利用規約</a>に同意する
                    </span>
                  }
                  onChange={(event) => setTosChecked(event.currentTarget.checked)}></Checkbox>
              <Checkbox
                  color="#0909FF"
                  checked={privacyChecked}
                  label={
                    <span>
                      <a className="text-blue-600 underline" href="https://drop4i.com/privacy-policy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>に同意する
                    </span>
                  }
                  onChange={(event) => setPrivacyChecked(event.currentTarget.checked)}></Checkbox>
            </div>
          </div>
          <div className="mt-4">
            {GoogleAuthComponent()}
          </div>
          <div className="mr-4 text-center mt-3">
            <button
              disabled={!hasAgreedToPolicies}
              onClick={appleRegister}
              className="mx-auto max-w-[300px] flex py-2 px-2 w-full justify-center items-center bg-white text-black border rounded"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 14 17"
                width="18"
                height="18"
                fill="currentColor"
                className="mr-2"
                aria-hidden="true"
              >
                <path d="M10.243 9.088c-.015-1.626 1.328-2.403 1.387-2.44-.757-1.1-1.934-1.25-2.352-1.267-1-.102-1.95.586-2.457.586-.514 0-1.286-.572-2.12-.557-1.089.015-2.095.634-2.656 1.607-1.135 1.965-.29 4.862.815 6.455.54.785 1.18 1.662 2.02 1.632.815-.03 1.12-.525 2.103-.525.976 0 1.26.525 2.12.51.876-.015 1.432-.8 1.971-1.592.62-.907.876-1.78.892-1.826-.02-.007-1.707-.653-1.724-2.083zM8.72 3.96c.45-.544.756-1.3.674-2.06-.653.026-1.438.435-1.901.979-.418.482-.784 1.247-.687 1.987.728.056 1.464-.37 1.914-.905z"/>
              </svg>
              <span className="text-sm">Appleで登録</span>
            </button>
          </div>
          <div className="text-xs mt-4 text-gray-500">
            ※アカウント登録でFreeプランが自動適用されます。<br/>
            Freeプランは無期限で無料利用が可能です。
          </div>
          <div className="text-xs mt-3 mx-auto text-gray-500">
            <ul className="list-disc list-inside text-left w-fit mx-auto">
              <li className="list-none">Freeプラン提供内容</li>
              <li>1日あたりの付与クレジット数 30</li>
              <li>広告非表示オプション なし</li>
              <li>同時画像生成数 1 まで</li>
              <li>生成画像の保存期間 30 日間</li>
              <li>詳細画像設定機能 なし</li>
            </ul>
          </div>
          <div className="mt-8">
          <span onClick={() => setIsLoginMode(true) } className="text-sm clickable text-blue-600">アカウントをすでにお持ちの方はこちら</span>
          </div>
        </div>
        }
        <div className={`relative max-h-[200px] sm:max-h-[600px] w-full ${isLoginMode ? '' : 'hidden sm:block'}`}>
          <img src="/images/login-left-image.png" className="object-cover w-full h-[160px] sm:h-[460px] object-top"></img>
          <div className="absolute left-0 right-0 bottom-0 h-[200px] sm:h-[400px] w-full" style={{ background : 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 40%, rgba(255, 255, 255, 1) 150%)' }}></div>
        </div>
        
      </div>
    </div>
  </>;

}
